import { useCallback, useEffect, useRef, useState } from "react";
import { useLoadingTiming } from "./use-loading-timing";

export const useSequentialTransition = (items: string[], interval: number) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const currentIndexRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get timing calculations
  const timing = useLoadingTiming(interval);

  // Keep ref in sync with state
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  // Reset state when props change
  useEffect(() => {
    setCurrentIndex(0);
    setIsChanging(false);
  }, [items, timing.normalizedInterval]);

  // Transition logic
  const transitionToNext = useCallback(() => {
    const nextIndex = currentIndexRef.current + 1;

    if (nextIndex >= items.length) {
      return false; // No transition needed
    }

    setIsChanging(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setCurrentIndex(nextIndex);
      setIsChanging(false);
      timeoutRef.current = null;
    }, timing.fadeDelay);

    return true; // Transition started
  }, [items.length, timing.fadeDelay]);

  // Main progression effect
  useEffect(() => {
    if (items.length <= 1) {
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;

    intervalId = setInterval(() => {
      const shouldContinue = transitionToNext();

      if (!shouldContinue && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }, timing.normalizedInterval);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [items.length, timing.normalizedInterval, transitionToNext]);

  return {
    currentItem: items[currentIndex],
    isChanging,
    pulseDuration: timing.pulseDuration,
  };
};
